import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { EmptyState, LoadingState, MetricCard, PageHeader, TableToolbar } from './AdminUI';

const tableStyles = {
    headCells: {
        style: {
            padding: '12px 16px',
            fontWeight: '600',
            fontSize: '13px',
            color: '#666666',
            backgroundColor: '#f8fafc',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        }
    },
    cells: {
        style: {
            padding: '14px 16px',
            fontSize: '14px',
            color: '#222222'
        }
    },
    rows: {
        style: {
            minHeight: '56px'
        }
    }
};

const formatDateTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
};

const titleCase = (value) => String(value || '-').replace(/_/g, ' ');

const actionClass = (action) => {
    if (action === 'login') return 'audit-action audit-action-login';
    if (action === 'delete') return 'audit-action audit-action-delete';
    if (action === 'update' || action === 'change_password') return 'audit-action audit-action-update';
    return 'audit-action audit-action-create';
};

const AuditLogs = () => {
    const { apiCall } = useAuth();
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tableSearch, setTableSearch] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchAuditLogs = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiCall('/audit-logs?limit=500');
            if (!response.ok) {
                throw new Error('Could not load audit logs');
            }

            const data = await response.json();
            setAuditLogs(Array.isArray(data.auditLogs) ? data.auditLogs : []);
        } catch (err) {
            console.error('Error fetching audit logs:', err);
            setAuditLogs([]);
            setError(err.message || 'Could not load audit logs');
        } finally {
            setLoading(false);
        }
    }, [apiCall]);

    useEffect(() => {
        fetchAuditLogs();
    }, [fetchAuditLogs]);

    const filteredLogs = useMemo(() => {
        const query = tableSearch.trim().toLowerCase();
        if (!query) return auditLogs;

        return auditLogs.filter((log) => [
            log.username,
            log.email,
            log.role,
            log.action,
            log.resource,
            log.path,
            log.method,
            log.ipAddress
        ].some((value) => String(value || '').toLowerCase().includes(query)));
    }, [auditLogs, tableSearch]);

    const counts = useMemo(() => ({
        total: auditLogs.length,
        logins: auditLogs.filter((log) => log.action === 'login').length,
        changes: auditLogs.filter((log) => ['create', 'update', 'delete', 'change_password', 'register'].includes(log.action)).length,
        users: new Set(auditLogs.map((log) => log.email).filter(Boolean)).size
    }), [auditLogs]);

    const columns = [
        {
            name: 'User',
            selector: (row) => row.username || row.email || '-',
            sortable: true,
            minWidth: '210px',
            cell: (row) => (
                <div>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{row.username || 'Unknown user'}</div>
                    <div style={{ fontSize: '12px', color: '#718096' }}>{row.email || '-'}</div>
                </div>
            )
        },
        {
            name: 'Role',
            selector: (row) => row.role || '-',
            sortable: true,
            width: '120px',
            cell: (row) => <span className="status-badge status-contacted">{row.role || '-'}</span>
        },
        {
            name: 'Action',
            selector: (row) => row.action,
            sortable: true,
            width: '160px',
            cell: (row) => <span className={actionClass(row.action)}>{titleCase(row.action)}</span>
        },
        {
            name: 'Resource',
            selector: (row) => row.resource || '-',
            sortable: true,
            width: '150px',
            cell: (row) => (
                <div>
                    <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{row.resource || '-'}</div>
                    <div style={{ fontSize: '12px', color: '#718096' }}>{row.method || '-'}</div>
                </div>
            )
        },
        {
            name: 'Path',
            selector: (row) => row.path || '-',
            sortable: true,
            minWidth: '240px',
            cell: (row) => <span className="analytics-path-cell">{row.path || '-'}</span>
        },
        {
            name: 'IP',
            selector: (row) => row.ipAddress || '-',
            sortable: true,
            width: '150px'
        },
        {
            name: 'Time',
            selector: (row) => row.createdAt,
            sortable: true,
            minWidth: '190px',
            cell: (row) => formatDateTime(row.createdAt)
        }
    ];

    return (
        <AdminLayout>
            <div className="analytics-page">
                <PageHeader
                    title="Audit Logs"
                    subtitle="Track who logs in and what each user changes."
                />

                {error ? (
                    <div className="analytics-alert">
                        <i className="fa fa-exclamation-triangle"></i>
                        {error}
                    </div>
                ) : null}

                {loading ? (
                    <LoadingState label="Loading audit logs..." />
                ) : (
                    <>
                        <div className="analytics-metrics-grid">
                            <MetricCard label="Total Records" value={counts.total} icon="fa fa-list" accent="#62B6E8" detail="Latest 500 events" />
                            <MetricCard label="Logins" value={counts.logins} icon="fa fa-sign-in" accent="#10b981" detail="Successful sign-ins" />
                            <MetricCard label="Changes" value={counts.changes} icon="fa fa-pencil" accent="#f59e0b" detail="Create, update, delete" />
                            <MetricCard label="Users" value={counts.users} icon="fa fa-users" accent="#8b5cf6" detail="Unique usernames" />
                        </div>

                        <section className="card admin-data-card">
                            <TableToolbar
                                search={tableSearch}
                                onSearchChange={setTableSearch}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={setRowsPerPage}
                                placeholder="Search audit records..."
                            />
                            <DataTable
                                columns={columns}
                                data={filteredLogs}
                                pagination
                                paginationPerPage={rowsPerPage}
                                paginationRowsPerPageOptions={[10, 20, 30]}
                                paginationComponentOptions={{ noRowsPerPage: true }}
                                customStyles={tableStyles}
                                noDataComponent={
                                    <EmptyState
                                        icon="fa fa-history"
                                        title="No audit records found"
                                        text="New logins and admin changes will appear here."
                                    />
                                }
                                highlightOnHover
                                pointerOnHover
                            />
                        </section>
                    </>
                )}
            </div>
        </AdminLayout>
    );
};

export default AuditLogs;
